import React, { useState, useEffect, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  Zap, Plus, MessageSquare, Send, Trash2, Edit2, ShieldCheck, Cpu, RefreshCw, X, Instagram, MessageCircle, Copy,
  Terminal, ArrowDownCircle, ArrowUpCircle,
} from 'lucide-react';
import { Button } from '../../components/ui/Button';
import { cn } from '../../utils/cn';
import api from '../../services/api';
import alertService from '../../utils/alert';
import DashboardModuleShell from '../../components/DashboardModuleShell';
import { fetchLivePosts } from '../../redux/instagramSlice';
import CopyRuleToPlatformModal from '../../components/automation/CopyRuleToPlatformModal';

const BLANK_FORM = {
  name: '',
  channel: 'comment',
  scope: 'all_posts',
  postIds: [],
  matchingType: 'contains',
  keywords: '',
  actionType: 'private_reply',
  responseTemplate: '',
  campaignTag: '',
  priority: 0,
  isActive: true,
  requireFollower: false,
};

const STATUS_BADGE_CLASS = {
  success: 'sf-badge-success',
  error: 'sf-badge-error',
  skipped: 'sf-badge-warning',
  info: 'sf-badge-neutral',
};

// Every inbound webhook event and every outbound automation/AI-agent send
// attempt, newest first — lets a tenant confirm the webhook is actually
// firing and see exactly why a given comment/DM did or didn't get a reply,
// without needing server access to read raw logs.
function InstagramActivityLogPanel() {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [expandedId, setExpandedId] = useState(null);

  const fetchLogs = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get('/meta-instagram/automation/logs', { params: { limit: 100 } });
      setLogs(res.data || []);
    } catch (err) {
      alertService.error('Load Failed', err.response?.data?.error || 'Failed to load activity logs.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchLogs(); }, [fetchLogs]);

  return (
    <div className="sf-card">
      <div className="sf-card-header flex items-center justify-between">
        <h3 className="sf-card-title flex items-center gap-2"><Terminal size={16} /> Webhook &amp; Automation Activity Log</h3>
        <Button variant="outline" size="sm" icon={RefreshCw} onClick={fetchLogs} loading={loading}>Refresh</Button>
      </div>
      <div className="p-4">
        <p className="text-[12px] text-[#5C5C5C] mb-4">
          Every comment/DM Meta sends to this account, and every automated reply attempt — newest first. Use this to
          confirm the webhook is actually firing, and to see exactly why a rule did or didn't reply (no match, in
          cooldown, not a confirmed follower, or a send failure). Entries auto-expire after 30 days.
        </p>
        {loading && logs.length === 0 ? (
          <div className="py-10 text-center text-[12px] text-[#5C5C5C]">Loading…</div>
        ) : logs.length === 0 ? (
          <div className="py-10 text-center text-[12px] text-[#5C5C5C]">
            No activity recorded yet — this fills in as soon as Meta sends a webhook event to this account.
          </div>
        ) : (
          <div className="max-h-[480px] overflow-y-auto divide-y divide-[#EEF1F6]">
            {logs.map((log) => (
              <div key={log._id} className="py-2.5">
                <button
                  type="button"
                  onClick={() => setExpandedId(expandedId === log._id ? null : log._id)}
                  className="w-full flex items-center gap-3 text-left"
                >
                  {log.direction === 'inbound'
                    ? <ArrowDownCircle size={15} className="text-[#0176D3] shrink-0" />
                    : <ArrowUpCircle size={15} className="text-[#8E44EC] shrink-0" />}
                  <span className={cn('sf-badge shrink-0', STATUS_BADGE_CLASS[log.status] || 'sf-badge-neutral')}>{log.status}</span>
                  <span className="text-[11px] font-semibold text-[#706E6B] shrink-0">{log.channel}</span>
                  <span className="text-[12.5px] text-[#2E2E2E] truncate flex-1">{log.summary}</span>
                  <span className="text-[11px] text-[#5C5C5C] shrink-0">{new Date(log.createdAt).toLocaleString()}</span>
                </button>
                {expandedId === log._id && log.detail && (
                  <pre className="mt-2 ml-6 p-3 bg-[#F4F6F9] border border-[#EEF1F6] rounded text-[11px] text-[#2E2E2E] overflow-x-auto whitespace-pre-wrap break-all">
                    {JSON.stringify(log.detail, null, 2)}
                  </pre>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

export default function InstagramAutomationPage() {
  const dispatch = useDispatch();
  const location = useLocation();
  const navigate = useNavigate();
  // livePosts is the account's real Instagram media, fetched straight from
  // Graph API — previously this picker only used the local `posts` list
  // (composed/published through this app's own composer), so anything
  // published directly on Instagram, or before this feature existed, never
  // showed up as a scoping target at all.
  const { livePosts, livePostsLoading } = useSelector((s) => s.instagram);

  const [rules, setRules] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [editingRule, setEditingRule] = useState(null);
  const [formData, setFormData] = useState(BLANK_FORM);
  const [copyRule, setCopyRule] = useState(null);

  const fetchRules = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get('/meta-instagram/automation');
      setRules(res.data || []);
    } catch (err) {
      alertService.error('Fetch Failed', 'Could not retrieve automation rules.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchRules();
    dispatch(fetchLivePosts());
  }, [fetchRules, dispatch]);

  // A rule duplicated from another platform arrives here as router state —
  // open the New Rule form pre-filled with it, then clear the state so a
  // refresh or back-navigation doesn't re-open it.
  useEffect(() => {
    if (location.state?.prefillRule) {
      const { prefillRule } = location.state;
      setEditingRule(null);
      setFormData({
        ...BLANK_FORM,
        ...prefillRule,
        keywords: (prefillRule.keywords || []).join(', '),
      });
      setShowForm(true);
      navigate(location.pathname, { replace: true, state: {} });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.state]);

  const openCreate = () => {
    setEditingRule(null);
    setFormData(BLANK_FORM);
    setShowForm(true);
  };

  const openEdit = (rule) => {
    setEditingRule(rule);
    setFormData({
      ...rule,
      keywords: (rule.keywords || []).join(', '),
      postIds: (rule.postIds || []).map((p) => (typeof p === 'string' ? p : p._id)),
    });
    setShowForm(true);
  };

  const handleSave = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const payload = {
        ...formData,
        keywords: typeof formData.keywords === 'string'
          ? formData.keywords.split(',').map((k) => k.trim()).filter(Boolean)
          : formData.keywords,
        // DMs are always account-wide — no post context exists on a DM webhook event.
        postIds: formData.channel === 'dm' || formData.scope === 'all_posts' ? [] : formData.postIds,
        actionType: formData.channel === 'dm' ? 'send_dm' : formData.actionType,
      };

      if (editingRule) {
        await api.put(`/meta-instagram/automation/${editingRule._id}`, payload);
        alertService.toast('success', 'Automation rule updated.');
      } else {
        await api.post('/meta-instagram/automation', payload);
        alertService.toast('success', 'Automation rule created.');
      }
      setShowForm(false);
      fetchRules();
    } catch (err) {
      alertService.error('Save Failed', err.response?.data?.error || 'Could not save automation rule.');
    } finally {
      setLoading(false);
    }
  };

  const toggleStatus = async (rule) => {
    try {
      await api.put(`/meta-instagram/automation/${rule._id}`, { isActive: !rule.isActive });
      fetchRules();
      alertService.toast('info', `Rule ${rule.isActive ? 'paused' : 'resumed'}.`);
    } catch (err) {
      alertService.error('Status Error', 'Failed to toggle rule state.');
    }
  };

  const deleteRule = async (id) => {
    const confirmed = await alertService.confirm('Delete Rule', 'Are you sure you want to delete this automation rule?');
    if (!confirmed) return;
    try {
      await api.delete(`/meta-instagram/automation/${id}`);
      alertService.toast('success', 'Rule deleted successfully.');
      fetchRules();
    } catch (err) {
      alertService.error('Delete Failed', 'Could not remove the specified rule.');
    }
  };

  const togglePostSelection = (postId) => {
    setFormData((f) => ({
      ...f,
      postIds: f.postIds.includes(postId) ? f.postIds.filter((id) => id !== postId) : [...f.postIds, postId],
    }));
  };

  const stats = [
    { label: 'Active Rules', value: rules.filter((r) => r.isActive).length, icon: Zap, color: 'text-[#04844B]' },
    { label: 'Comment Rules', value: rules.filter((r) => r.channel === 'comment').length, icon: MessageCircle, color: 'text-[#0176D3]' },
    { label: 'DM Rules', value: rules.filter((r) => r.channel === 'dm').length, icon: Send, color: 'text-[#0176D3]' },
  ];

  return (
    <DashboardModuleShell
      title="Instagram Automation"
      subtitle="Auto-reply to comments and DMs — scope a rule to a specific post or campaign, or apply it account-wide."
      icon={Instagram}
      stats={stats}
      actions={
        <div className="flex items-center gap-3">
          <Button variant="outline" icon={RefreshCw} onClick={fetchRules} loading={loading}>Refresh</Button>
          <Button onClick={openCreate} icon={Plus}>New Rule</Button>
        </div>
      }
    >
      <div className="space-y-6">
        <div className="sf-card">
          <div className="sf-card-header flex items-center justify-between">
            <h3 className="sf-card-title">Automation Rules</h3>
            <span className="sf-badge sf-badge-manager">{rules.length} Rules</span>
          </div>

          <div className="sf-table-container border-0 shadow-none">
            <div className="overflow-x-auto">
              <table className="sf-table">
                <thead>
                  <tr>
                    <th>Rule Name</th>
                    <th>Channel</th>
                    <th>Scope</th>
                    <th>Keywords</th>
                    <th>Action</th>
                    <th>Status</th>
                    <th className="text-right">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {loading && rules.length === 0 ? (
                    <tr>
                      <td colSpan={7} className="py-16 text-center">
                        <RefreshCw className="w-8 h-8 animate-spin text-[#0176D3] mx-auto mb-3" />
                        <p className="text-[11px] font-semibold text-[#706E6B]">Loading rules…</p>
                      </td>
                    </tr>
                  ) : rules.length === 0 ? (
                    <tr>
                      <td colSpan={7} className="py-16 text-center px-6">
                        <div className="w-16 h-16 rounded-md bg-[#F4F6F9] border border-[#E5E5E5] flex items-center justify-center mb-4 mx-auto">
                          <Zap className="w-8 h-8 text-[#5C5C5C]" />
                        </div>
                        <h3 className="text-[14px] font-bold text-[#2E2E2E] mb-1">No Automation Rules Yet</h3>
                        <p className="text-[13px] text-[#5C5C5C] mb-6 max-w-sm mx-auto">Create a rule to auto-reply to comments on a specific post, or to DMs containing a keyword.</p>
                        <Button onClick={openCreate} icon={Plus} variant="outline">Create First Rule</Button>
                      </td>
                    </tr>
                  ) : (
                    rules.map((rule) => (
                      <tr key={rule._id}>
                        <td>
                          <div className="flex items-center gap-3">
                            <div className={cn(
                              'w-8 h-8 rounded-md flex items-center justify-center shrink-0',
                              rule.isActive ? 'bg-[#EAF4FE] text-[#0176D3]' : 'bg-[#F3F2F2] text-[#5C5C5C]'
                            )}>
                              <Cpu size={16} />
                            </div>
                            <div>
                              <p className="text-[13px] font-bold text-[#2E2E2E]">{rule.name}</p>
                              <p className="text-[11px] text-[#5C5C5C]">
                                Priority: {rule.priority}{rule.campaignTag ? ` · ${rule.campaignTag}` : ''}
                              </p>
                            </div>
                          </div>
                        </td>
                        <td>
                          <span className="sf-badge sf-badge-manager">{rule.channel === 'comment' ? 'Comment' : 'DM'}</span>
                        </td>
                        <td className="text-[12px] text-[#5C5C5C]">
                          {rule.channel === 'dm' ? 'Account-wide' : rule.scope === 'all_posts' ? 'All Posts' : `${(rule.postIds || []).length} post(s)`}
                        </td>
                        <td>
                          <div className="flex flex-wrap gap-1.5 items-center">
                            {rule.matchingType === 'any' ? (
                              <span className="text-[11px] text-[#5C5C5C] italic">Any (no keyword)</span>
                            ) : (
                              <>
                                {(rule.keywords || []).map((k, i) => (
                                  <span key={i} className="sf-badge sf-badge-manager">{k}</span>
                                ))}
                                <span className="text-[11px] text-[#5C5C5C]">({rule.matchingType})</span>
                              </>
                            )}
                          </div>
                        </td>
                        <td>
                          <div className="flex items-center gap-2">
                            <div className="w-7 h-7 bg-[#F4F6F9] rounded border border-[#E5E5E5] flex items-center justify-center text-[#5C5C5C]">
                              {rule.actionType === 'public_reply' ? <MessageSquare size={14} /> : <Send size={14} />}
                            </div>
                            <span className="text-[13px] font-medium text-[#2E2E2E]">
                              {rule.actionType.replace('_', ' ')}
                            </span>
                          </div>
                        </td>
                        <td>
                          <button
                            onClick={() => toggleStatus(rule)}
                            className={cn('sf-badge', rule.isActive ? 'sf-badge-success' : 'sf-badge-error')}
                          >
                            {rule.isActive ? 'Active' : 'Paused'}
                          </button>
                        </td>
                        <td className="text-right">
                          <div className="flex justify-end gap-1">
                            <button onClick={() => setCopyRule(rule)} title="Duplicate to…" aria-label="Duplicate rule to another platform" className="p-1.5 text-[#5C5C5C] hover:text-[#0176D3] hover:bg-[#F3F2F2] rounded transition-colors">
                              <Copy size={14} />
                            </button>
                            <button onClick={() => openEdit(rule)} title="Edit" aria-label="Edit rule" className="p-1.5 text-[#5C5C5C] hover:text-[#0176D3] hover:bg-[#F3F2F2] rounded transition-colors">
                              <Edit2 size={14} />
                            </button>
                            <button onClick={() => deleteRule(rule._id)} title="Delete" aria-label="Delete rule" className="p-1.5 text-[#5C5C5C] hover:text-[#BA0517] hover:bg-[#FDE7E9] rounded transition-colors">
                              <Trash2 size={14} />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>

        <div className="sf-card p-4 flex items-start gap-3">
          <ShieldCheck className="text-[#0176D3] mt-0.5 shrink-0" size={18} />
          <div>
            <h4 className="text-[13px] font-bold text-[#2E2E2E]">How this works</h4>
            <p className="text-[12px] text-[#5C5C5C] leading-relaxed mb-0">
              Comment rules scoped to specific posts act as your "campaign" targeting — e.g. a rule scoped only to your
              Diwali Sale post replies with that offer, while a separate all-posts rule handles everything else. When
              several rules match the same comment or DM, only the highest-priority one replies — priority is the
              tie-breaker (the lowest priority number, e.g. "1", wins), not a queue of replies to send. DM rules run
              before the AI chatbot — if a DM matches a rule, that reply is sent instead of the AI's general-purpose
              response.
            </p>
          </div>
        </div>

        <InstagramActivityLogPanel />
      </div>

      {showForm && (
        <div className="fixed inset-0 z-[200] flex items-end sm:items-center justify-center bg-black/40 sm:p-4">
          <div className="bg-white w-full max-w-2xl rounded-t-lg sm:rounded-lg shadow-[0_2px_8px_rgba(0,0,0,0.16)] flex flex-col overflow-hidden max-h-[100dvh] sm:max-h-[90vh]">
            <div className="px-4 sm:px-6 py-3 flex items-center justify-between border-b border-[#E5E5E5] shrink-0">
              <h3 className="text-[16px] font-bold text-[#2E2E2E]">
                {editingRule ? 'Edit Automation Rule' : 'New Automation Rule'}
              </h3>
              <button onClick={() => setShowForm(false)} className="w-8 h-8 flex items-center justify-center hover:bg-white rounded transition-all border border-transparent hover:border-[#E5E5E5] text-[#5C5C5C]">
                <X size={16} />
              </button>
            </div>

            <form onSubmit={handleSave} className="flex-1 p-4 sm:p-6 overflow-y-auto space-y-6">
              <div className="space-y-2">
                <label className="text-[11px] font-semibold text-[#706E6B]">Rule Name</label>
                <input
                  type="text" required value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  placeholder="e.g. Diwali Sale Comment Offer"
                  className="w-full h-9 px-3 bg-white border border-[#D8DDE6] rounded text-[13px] outline-none focus:border-[#0176D3] focus:ring-1 focus:ring-[#0176D3] transition-all"
                />
              </div>

              <div className="space-y-2">
                <label className="text-[11px] font-semibold text-[#706E6B] block">Trigger On</label>
                <div className="flex gap-3">
                  {[
                    { id: 'comment', label: 'Comment', icon: MessageCircle },
                    { id: 'dm', label: 'Direct Message', icon: Send },
                  ].map((c) => (
                    <button
                      key={c.id} type="button"
                      onClick={() => setFormData({ ...formData, channel: c.id, actionType: c.id === 'dm' ? 'send_dm' : 'private_reply', requireFollower: c.id === 'dm' ? false : formData.requireFollower })}
                      className={cn(
                        'flex-1 p-3 rounded-md border text-[12px] font-bold transition-all flex flex-col items-center gap-2',
                        formData.channel === c.id ? 'bg-[#EAF4FE] border-[#0176D3] text-[#0176D3]' : 'bg-white border-[#E5E5E5] text-[#5C5C5C] hover:border-[#0176D3]'
                      )}
                    >
                      <c.icon size={18} />
                      {c.label}
                    </button>
                  ))}
                </div>
              </div>

              {formData.channel === 'comment' && (
                <div className="space-y-3 pt-2">
                  <label className="text-[11px] font-semibold text-[#706E6B] block">Applies To</label>
                  <div className="flex gap-3">
                    {[
                      { id: 'all_posts', label: 'All Posts' },
                      { id: 'specific_posts', label: 'Specific Post(s) — your campaign' },
                    ].map((s) => (
                      <button
                        key={s.id} type="button"
                        onClick={() => setFormData({ ...formData, scope: s.id })}
                        className={cn(
                          'flex-1 p-2.5 rounded-md border text-[12px] font-bold transition-all',
                          formData.scope === s.id ? 'bg-[#EAF4FE] border-[#0176D3] text-[#0176D3]' : 'bg-white border-[#E5E5E5] text-[#5C5C5C] hover:border-[#0176D3]'
                        )}
                      >
                        {s.label}
                      </button>
                    ))}
                  </div>

                  {formData.scope === 'specific_posts' && (
                    <div className="max-h-56 overflow-y-auto border border-[#E5E5E5] rounded-md p-2 grid grid-cols-1 sm:grid-cols-2 gap-2">
                      {livePostsLoading && (livePosts || []).length === 0 && (
                        <p className="text-[12px] text-[#5C5C5C] col-span-2 p-3">Loading your posts…</p>
                      )}
                      {!livePostsLoading && (livePosts || []).filter((p) => p.localPostId).length === 0 && (
                        <p className="text-[12px] text-[#5C5C5C] col-span-2 p-3">No posts found on this Instagram account yet — publish a post first, then scope a rule to it.</p>
                      )}
                      {(livePosts || []).filter((p) => p.localPostId).map((p) => (
                        <button
                          type="button" key={p.localPostId}
                          onClick={() => togglePostSelection(p.localPostId)}
                          className={cn(
                            'flex gap-2 items-center p-2 rounded-md border text-left transition-all',
                            formData.postIds.includes(p.localPostId) ? 'border-[#0176D3] bg-[#EAF4FE]' : 'border-[#E5E5E5] bg-white hover:border-[#0176D3]'
                          )}
                        >
                          {(p.media_type === 'VIDEO' ? p.thumbnail_url : p.media_url) && (
                            <img src={p.media_type === 'VIDEO' ? p.thumbnail_url : p.media_url} alt="" className="w-10 h-10 rounded object-cover flex-shrink-0" />
                          )}
                          <span className="text-[11.5px] text-[#2E2E2E] truncate">{p.caption || 'No caption'}</span>
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              )}

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <label className="text-[11px] font-semibold text-[#706E6B] block">Matching Type</label>
                  <select
                    className="w-full h-10 px-3 bg-white border border-[#E5E5E5] rounded text-[13px] outline-none cursor-pointer focus:border-[#0176D3] transition-all"
                    value={formData.matchingType}
                    onChange={(e) => setFormData({ ...formData, matchingType: e.target.value })}
                  >
                    <option value="any">Any (reply to everyone)</option>
                    <option value="contains">Contains Keyword</option>
                    <option value="exact">Exact Match</option>
                    <option value="regex">Pattern (Regex)</option>
                  </select>
                </div>
                <div className="space-y-2">
                  <label className="text-[11px] font-semibold text-[#706E6B] block">Priority</label>
                  <input
                    type="number" value={formData.priority}
                    onChange={(e) => setFormData({ ...formData, priority: parseInt(e.target.value, 10) || 0 })}
                    className="w-full h-9 px-3 bg-white border border-[#D8DDE6] rounded text-[13px] outline-none focus:border-[#0176D3] focus:ring-1 focus:ring-[#0176D3] transition-all"
                  />
                </div>
              </div>

              {formData.matchingType !== 'any' && (
                <div className="space-y-2">
                  <label className="text-[11px] font-semibold text-[#706E6B] block">Keywords (comma separated)</label>
                  <input
                    type="text" required value={formData.keywords}
                    onChange={(e) => setFormData({ ...formData, keywords: e.target.value })}
                    placeholder="price, discount, offer"
                    className="w-full h-9 px-3 bg-white border border-[#D8DDE6] rounded text-[13px] outline-none focus:border-[#0176D3] focus:ring-1 focus:ring-[#0176D3] transition-all"
                  />
                </div>
              )}

              {formData.channel === 'comment' && (
                <div className="space-y-2">
                  <label className="text-[11px] font-semibold text-[#706E6B] block">Reply As</label>
                  <div className="flex gap-3">
                    {[
                      { id: 'public_reply', label: 'Public Reply', desc: 'Threaded under the comment' },
                      { id: 'private_reply', label: 'Private Reply (DM)', desc: 'Turns the comment into a DM' },
                    ].map((a) => (
                      <button
                        key={a.id} type="button"
                        onClick={() => setFormData({ ...formData, actionType: a.id })}
                        className={cn(
                          'flex-1 p-2.5 rounded-md border text-left transition-all',
                          formData.actionType === a.id ? 'bg-[#EAF4FE] border-[#0176D3]' : 'bg-white border-[#E5E5E5] hover:border-[#0176D3]'
                        )}
                      >
                        <p className={cn('text-[12px] font-bold', formData.actionType === a.id ? 'text-[#0176D3]' : 'text-[#2E2E2E]')}>{a.label}</p>
                        <p className="text-[11px] text-[#5C5C5C]">{a.desc}</p>
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {formData.channel === 'comment' && formData.actionType === 'private_reply' && (
                <label className="flex items-start gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={formData.requireFollower}
                    onChange={(e) => setFormData({ ...formData, requireFollower: e.target.checked })}
                    className="mt-0.5"
                  />
                  <span className="text-[12px] text-[#5C5C5C]">
                    <span className="font-bold text-[#2E2E2E]">Only send this DM if they follow this account.</span> Checks with Meta
                    before sending. If that can't be determined — most commonly when the account is connected via Instagram Login
                    instead of a linked Facebook Page — the message is <span className="font-bold">not sent</span> rather than sent to
                    an unverified commenter.
                  </span>
                </label>
              )}

              <div className="space-y-2">
                <label className="text-[11px] font-semibold text-[#706E6B] block">
                  {formData.channel === 'comment' ? 'Reply Message' : 'DM Message'}
                </label>
                <textarea
                  rows={4} required value={formData.responseTemplate}
                  onChange={(e) => setFormData({ ...formData, responseTemplate: e.target.value })}
                  placeholder="Thanks for your interest, {{username}}! Here's 20% off with code SALE20..."
                  className="w-full px-3 py-3 bg-white border border-[#E5E5E5] rounded-md text-[13px] leading-relaxed outline-none focus:border-[#0176D3] transition-all resize-none"
                />
                <p className="text-[11px] text-[#5C5C5C]">Use <code className="bg-[#F4F6F9] px-1 rounded">{'{{username}}'}</code> to personalize with the commenter/sender's name.</p>
              </div>

              <div className="space-y-2">
                <label className="text-[11px] font-semibold text-[#706E6B] block">Campaign Tag (optional)</label>
                <input
                  type="text" value={formData.campaignTag}
                  onChange={(e) => setFormData({ ...formData, campaignTag: e.target.value })}
                  placeholder="e.g. Diwali Sale 2026"
                  className="w-full h-9 px-3 bg-white border border-[#D8DDE6] rounded text-[13px] outline-none focus:border-[#0176D3] focus:ring-1 focus:ring-[#0176D3] transition-all"
                />
                <p className="text-[11px] text-[#5C5C5C]">Just a label to help you find this rule later — shown in the rules list.</p>
              </div>

              <div className="pt-4 border-t border-[#E5E5E5] flex justify-end gap-3">
                <Button variant="outline" type="button" onClick={() => setShowForm(false)}>Cancel</Button>
                <Button type="submit" loading={loading}>Save</Button>
              </div>
            </form>
          </div>
        </div>
      )}

      <CopyRuleToPlatformModal
        isOpen={!!copyRule}
        onClose={() => setCopyRule(null)}
        sourcePlatform="instagram"
        rule={copyRule}
      />
    </DashboardModuleShell>
  );
}
